(() => {
  class CountdownBarStorage {
    constructor() {
      this.localStorage = this.getLocalStorage();
    }

    getLocalStorage() {
      try {
        const key = '__countdown_bar_test__';
        window.localStorage.setItem(key, '1');
        window.localStorage.removeItem(key);
        return window.localStorage;
      } catch (e) {
        return null;
      }
    }

    getItem(key) {
      if (this.localStorage) return this.localStorage.getItem(key);
      return this.getCookie(key);
    }

    setItem(key, value) {
      if (this.localStorage) {
        this.localStorage.setItem(key, value);
        return;
      }
      document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; path=/; max-age=31536000`;
    }

    removeItem(key) {
      if (this.localStorage) {
        this.localStorage.removeItem(key);
        return;
      }
      document.cookie = `${encodeURIComponent(key)}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    keys() {
      if (this.localStorage) return Object.keys(this.localStorage);
      if (!document.cookie) return [];
      return document.cookie.split(';').map((item) => decodeURIComponent(item.split('=')[0].trim()));
    }

    getCookie(name) {
      const escaped = encodeURIComponent(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    }
  }

  const CountdownBarRuntime = {
    storage: new CountdownBarStorage(),
    instances: new Map(),

    register(element) {
      if (!element.dataset.blockId) return;
      this.instances.set(element.dataset.blockId, element);
      this.updateGlobalStates(element.getSectionElement());
    },

    unregister(element) {
      if (!element.dataset.blockId) return;
      this.instances.delete(element.dataset.blockId);
      this.updateGlobalStates(element.getSectionElement());
    },

    updateStickyState(sectionElement) {
      if (!(sectionElement instanceof HTMLElement)) return;
      const stickyTarget = sectionElement.closest('.shopify-section') || sectionElement;
      const hasStickyVisible = sectionElement.querySelector(
        'countdown-bar-item[data-is-sticky="true"].active:not(.hidden)'
      );
      stickyTarget.classList.toggle('countdown-section--sticky-parent', !!hasStickyVisible);
    },

    updateHeaderFooterVisibility() {
      const shouldHideLayout = Array.from(this.instances.values()).some((instance) => {
        return (
          instance.settings?.hideHeaderFooter &&
          instance.classList.contains('active') &&
          !instance.classList.contains('hidden')
        );
      });

      document.body.classList.toggle('countdown-bar-hide-layout', shouldHideLayout);
    },

    updateGlobalStates(sectionElement) {
      this.updateStickyState(sectionElement);
      this.updateHeaderFooterVisibility();
    },

    runUrlDebug() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('countdown_popup_debug') !== '1') return;

      const blockFilter = params.get('countdown_popup_block');
      this.instances.forEach((instance, blockId) => {
        if (blockFilter && blockFilter !== blockId) return;
        if (instance.settings?.countdownMode === 'duration' && instance.settings.popup?.enabled) {
          instance.showPopup(true);
        }
      });
    },

    resetDurationTimer(blockId) {
      const prefix = `countdown_duration_${blockId}_`;
      this.storage.keys().forEach((key) => {
        if (key.indexOf(prefix) === 0) {
          this.storage.removeItem(key);
        }
      });
    }
  };

  class CountdownBarItemElement extends HTMLElement {
    constructor() {
      super();
      this.settings = null;
      this.timerEl = null;
      this.intervalId = null;
      this.popupEscHandler = null;
      this.handlers = [];
      this.initialized = false;
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;

      this.timerEl = this.querySelector('.countdown-timer');
      this.settings = this.parseSettings();
      if (!this.settings) return;

      CountdownBarRuntime.register(this);

      if (
        this.settings.persistDismiss &&
        CountdownBarRuntime.storage.getItem(`countdown_bar_dismissed_${this.dataset.blockId}`) === 'true'
      ) {
        this.setHidden();
        return;
      }

      const targetInfo = this.getTargetDateInfo();
      if (!targetInfo.isValid) {
        this.setHidden();
        return;
      }

      if (this.settings.show_regardless || targetInfo.shouldShowBar) {
        this.setActive();
      } else {
        this.setHidden();
        return;
      }

      this.setupDismissButton();
      this.setupCopyButton();

      if (this.settings.showCountdown && this.timerEl && targetInfo.targetDate) {
        this.startTimer(targetInfo.targetDate);
      } else if (this.timerEl) {
        this.timerEl.style.display = 'none';
      }
    }

    destroy() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      if (this.popupEscHandler) {
        document.removeEventListener('keydown', this.popupEscHandler);
        this.popupEscHandler = null;
      }

      this.handlers.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.handlers = [];

      CountdownBarRuntime.unregister(this);
      this.initialized = false;
    }

    parseSettings() {
      const settingsNode = this.querySelector('.countdown-bar-settings');
      if (!settingsNode) return null;
      try {
        return JSON.parse(settingsNode.textContent);
      } catch (e) {
        return null;
      }
    }

    getSectionElement() {
      return this.closest('[data-section-type="countdown-bar-blocks"]');
    }

    addHandler(element, event, handler) {
      if (!element) return;
      element.addEventListener(event, handler);
      this.handlers.push({ element, event, handler });
    }

    setActive() {
      this.classList.add('active');
      this.classList.remove('hidden');
      CountdownBarRuntime.updateGlobalStates(this.getSectionElement());
    }

    setHidden() {
      this.classList.add('hidden');
      this.classList.remove('active');
      CountdownBarRuntime.updateGlobalStates(this.getSectionElement());
    }

    getTargetDateInfo() {
      if (this.settings.countdownMode === 'duration') {
        const targetDate = this.getDurationTargetDate();
        return { isValid: !!targetDate, shouldShowBar: !!targetDate, targetDate };
      }

      const startDate = this.parseCountdownDateTime(this.settings.startTime);
      const endDate = this.parseCountdownDateTime(this.settings.endTime);
      if (!startDate || !endDate) {
        return { isValid: false, shouldShowBar: false, targetDate: null };
      }

      const now = new Date();
      return {
        isValid: true,
        shouldShowBar: now >= startDate && now <= endDate,
        targetDate: endDate
      };
    }

    getDurationStorageKeys(totalDurationMs) {
      const pagePath = window.location && window.location.pathname ? window.location.pathname : '/';
      const pageKey = pagePath.replace(/[^a-zA-Z0-9/_-]/g, '_');
      const keyBase = `countdown_duration_${this.dataset.blockId}_${totalDurationMs}_${pageKey}`;
      return {
        endKey: `${keyBase}_end`,
        expiredKey: `${keyBase}_expired`
      };
    }

    getDurationMs() {
      return (
        ((parseInt(this.settings.duration?.days, 10) || 0) * 24 * 60 * 60 * 1000) +
        ((parseInt(this.settings.duration?.hours, 10) || 0) * 60 * 60 * 1000) +
        ((parseInt(this.settings.duration?.minutes, 10) || 0) * 60 * 1000) +
        ((parseInt(this.settings.duration?.seconds, 10) || 0) * 1000)
      );
    }

    getDurationTargetDate() {
      const durationMs = this.getDurationMs();
      if (durationMs <= 0) return null;

      const keys = this.getDurationStorageKeys(durationMs);
      if (CountdownBarRuntime.storage.getItem(keys.expiredKey) === 'true') {
        return new Date(0);
      }

      const storedEndTime = parseInt(CountdownBarRuntime.storage.getItem(keys.endKey), 10);
      if (!Number.isNaN(storedEndTime) && storedEndTime > Date.now()) {
        return new Date(storedEndTime);
      }

      if (!Number.isNaN(storedEndTime) && storedEndTime <= Date.now()) {
        CountdownBarRuntime.storage.setItem(keys.expiredKey, 'true');
        return new Date(storedEndTime);
      }

      const endTimestamp = Date.now() + durationMs;
      CountdownBarRuntime.storage.setItem(keys.endKey, String(endTimestamp));
      CountdownBarRuntime.storage.removeItem(keys.expiredKey);
      return new Date(endTimestamp);
    }

    markDurationExpired() {
      const durationMs = this.getDurationMs();
      if (durationMs <= 0) return;
      const keys = this.getDurationStorageKeys(durationMs);
      CountdownBarRuntime.storage.setItem(keys.expiredKey, 'true');
    }

    getEnabledUnits() {
      const units = {
        days: !!this.settings.enabledUnits?.days,
        hours: !!this.settings.enabledUnits?.hours,
        minutes: !!this.settings.enabledUnits?.minutes,
        seconds: !!this.settings.enabledUnits?.seconds
      };
      if (!units.days && !units.hours && !units.minutes && !units.seconds) {
        units.seconds = true;
      }
      return units;
    }

    getLabels() {
      return {
        days: this.settings.labels?.days || 'days',
        hours: this.settings.labels?.hours || 'hrs',
        minutes: this.settings.labels?.minutes || 'mins',
        seconds: this.settings.labels?.seconds || 'secs'
      };
    }

    getTimeByUnits(distanceMs, enabled) {
      const normalized = Math.max(0, distanceMs);
      let totalSeconds = Math.floor(normalized / 1000);

      let days = Math.floor(totalSeconds / 86400);
      totalSeconds -= days * 86400;
      let hours = Math.floor(totalSeconds / 3600);
      totalSeconds -= hours * 3600;
      let minutes = Math.floor(totalSeconds / 60);
      totalSeconds -= minutes * 60;
      let seconds = totalSeconds;

      if (!enabled.days) {
        hours += days * 24;
        days = 0;
      }
      if (!enabled.hours) {
        minutes += hours * 60;
        hours = 0;
      }
      if (!enabled.minutes) {
        seconds += minutes * 60;
        minutes = 0;
      }
      return { days, hours, minutes, seconds };
    }

    renderTimer(values, enabled, labels, isLoading = false) {
      if (!this.timerEl) return;
      const units = ['days', 'hours', 'minutes', 'seconds'].filter((unit) => enabled[unit]);
      const chunks = [];

      units.forEach((unit, index) => {
        const value = values[unit];
        const text = isLoading ? '--' : value < 10 ? `0${value}` : String(value);
        chunks.push(`
          <span class="timer-unit">
            <span class="timer-digit ${isLoading ? 'timer-loading' : ''}">${text}</span>
            <span class="timer-unit-label">${labels[unit]}</span>
          </span>
        `);
        if (index < units.length - 1) chunks.push('<div class="timer-separator">:</div>');
      });

      this.timerEl.innerHTML = chunks.join('');
    }

    startTimer(targetDate) {
      const enabled = this.getEnabledUnits();
      const labels = this.getLabels();

      this.timerEl.style.display = '';
      this.renderTimer({ days: 0, hours: 0, minutes: 0, seconds: 0 }, enabled, labels, true);

      const update = () => {
        const distance = targetDate.getTime() - Date.now();

        if (distance < 0) {
          if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
          }

          this.renderTimer({ days: 0, hours: 0, minutes: 0, seconds: 0 }, enabled, labels, false);
          if (this.settings.countdownMode === 'duration') {
            this.markDurationExpired();
            if (this.settings.popup?.enabled) this.showPopup();
          }
          return;
        }

        const values = this.getTimeByUnits(distance, enabled);
        this.renderTimer(values, enabled, labels, false);
      };

      update();
      this.intervalId = window.setInterval(update, 1000);
    }

    setupDismissButton() {
      const dismissButton = this.querySelector('[data-countdown-dismiss]');
      if (!dismissButton) return;
      this.addHandler(dismissButton, 'click', () => this.dismiss());
    }

    dismiss() {
      this.setHidden();

      if (this.settings.persistDismiss) {
        CountdownBarRuntime.storage.setItem(`countdown_bar_dismissed_${this.dataset.blockId}`, 'true');
      }

      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      const root = document.querySelector(':root');
      const updateOffsets = () => {
        const announcementHeight = document.querySelector('.shopify-section-countdown-bar-blocks')?.offsetHeight || 0;
        const headerHeight = document.querySelector('#custom-navbar')?.offsetHeight || 0;
        const total = announcementHeight + headerHeight;
        root.style.setProperty('--standard-offset', `${total}px`);
        root.style.setProperty('--mega-overlay-height-dt', `${total}px`);
      };
      updateOffsets();
      window.addEventListener('resize', updateOffsets);
    }

    setupCopyButton() {
      const copyButton = this.querySelector('.countdown-combined-button.copyable');
      if (!copyButton) return;

      this.addHandler(copyButton, 'click', (event) => {
        const textNode = copyButton.querySelector('.button-text');
        if (!textNode) return;
        event.preventDefault();
        const text = textNode.textContent.trim();
        if (!text) return;

        const pulse = () => {
          const originalOpacity = copyButton.style.opacity;
          copyButton.style.opacity = '0.7';
          setTimeout(() => {
            copyButton.style.opacity = originalOpacity || '1';
          }, 300);
        };

        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(text).then(pulse).catch(() => {});
          return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          pulse();
        } catch (e) {}
        document.body.removeChild(textarea);
      });
    }

    showPopup(previewMode = false) {
      if (!this.settings.popup?.enabled) return;

      const popup = this.querySelector('.countdown-expired-popup');
      if (!popup) return;

      const redirectUrl =
        typeof this.settings.popup.redirectUrl === 'string' && this.settings.popup.redirectUrl.trim() !== ''
          ? this.settings.popup.redirectUrl
          : '/';

      popup.classList.add('active');
      popup.setAttribute('aria-hidden', 'false');
      popup.dataset.previewMode = previewMode ? 'true' : 'false';
      document.body.classList.add('countdown-popup-open');

      const exitPopup = () => {
        if (popup.dataset.previewMode === 'true') {
          this.hidePopup();
          return;
        }
        window.location.href = redirectUrl;
      };

      if (popup.dataset.bound !== 'true') {
        const closeButton = popup.querySelector('[data-countdown-popup-close]');
        const actionButton = popup.querySelector('[data-countdown-popup-action]');
        this.addHandler(closeButton, 'click', exitPopup);
        this.addHandler(actionButton, 'click', (event) => {
          event.preventDefault();
          exitPopup();
        });
        this.addHandler(popup, 'click', (event) => {
          if (event.target === popup) exitPopup();
        });
        popup.dataset.bound = 'true';
      }

      if (this.popupEscHandler) {
        document.removeEventListener('keydown', this.popupEscHandler);
      }

      this.popupEscHandler = (event) => {
        if (event.key === 'Escape') exitPopup();
      };
      document.addEventListener('keydown', this.popupEscHandler);
    }

    hidePopup() {
      const popup = this.querySelector('.countdown-expired-popup');
      if (!popup) return;

      popup.classList.remove('active');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('countdown-popup-open');

      if (this.popupEscHandler) {
        document.removeEventListener('keydown', this.popupEscHandler);
        this.popupEscHandler = null;
      }
    }

    parseCountdownDateTime(str) {
      if (!str || typeof str !== 'string') return null;

      const [datePart, timePartRaw] = str.split(' ');
      const timePart = timePartRaw || '00:00:00';
      const dateComponents = datePart.split(/[/-]/);
      const timeComponents = timePart.split(':');

      if (dateComponents.length !== 3 || timeComponents.length < 2) return null;

      const day = parseInt(dateComponents[0], 10);
      const month = parseInt(dateComponents[1], 10) - 1;
      const year = parseInt(dateComponents[2], 10);
      const hour = parseInt(timeComponents[0], 10) || 0;
      const minute = parseInt(timeComponents[1], 10) || 0;
      const second = parseInt(timeComponents[2], 10) || 0;

      if ([day, month, year, hour, minute, second].some((value) => Number.isNaN(value))) return null;

      const tempDate = new Date(year, month, day);
      const isDST = this.checkPSTDaylightSaving(tempDate);
      const pstOffsetHours = isDST ? 7 : 8;

      return new Date(Date.UTC(year, month, day, hour + pstOffsetHours, minute, second));
    }

    checkPSTDaylightSaving(date) {
      const year = date.getFullYear();
      const march = new Date(year, 2, 1);
      const secondSundayMarch = new Date(year, 2, ((14 - march.getDay()) % 7) + 8);
      const november = new Date(year, 10, 1);
      const firstSundayNov = new Date(year, 10, ((7 - november.getDay()) % 7) + 1);
      return date >= secondSundayMarch && date < firstSundayNov;
    }
  }

  if (!customElements.get('countdown-bar-item')) {
    customElements.define('countdown-bar-item', CountdownBarItemElement);
  }

  function bindShopifyEvents() {
    document.addEventListener('shopify:section:load', () => {
      CountdownBarRuntime.runUrlDebug();
    });

    document.addEventListener('shopify:section:reorder', () => {
      CountdownBarRuntime.runUrlDebug();
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (!(window.Shopify && window.Shopify.designMode)) return;
      const block = event.target?.closest?.('countdown-bar-item[data-block-id]');
      if (!block) return;
      if (block.settings?.countdownMode === 'duration' && block.settings.popup?.enabled) {
        block.showPopup(true);
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const block = event.target?.closest?.('countdown-bar-item[data-block-id]');
      if (!block) return;
      block.hidePopup();
    });
  }

  function registerDebugApi() {
    if (window.CountdownBarDebug) return;
    window.CountdownBarDebug = {
      showOfferEndedPopup(blockId) {
        const instance = CountdownBarRuntime.instances.get(blockId);
        if (!instance || instance.settings?.countdownMode !== 'duration' || !instance.settings.popup?.enabled) return;
        instance.showPopup(true);
      },
      hideOfferEndedPopup(blockId) {
        const instance = CountdownBarRuntime.instances.get(blockId);
        if (!instance) return;
        instance.hidePopup();
      },
      resetDurationTimer(blockId) {
        if (!blockId) return;
        CountdownBarRuntime.resetDurationTimer(blockId);
      }
    };
  }

  function bootstrap() {
    bindShopifyEvents();
    registerDebugApi();
    CountdownBarRuntime.runUrlDebug();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
